<script lang="ts" setup>
import {computed, onBeforeMount} from "vue";
import {useDisplay} from 'vuetify';
import {useEntitiesStore} from "@/stores/entities.store.ts";
import DetailsCard from "@/components/Cards/Card/DetailsCard.vue";
import EntityAction from "@/components/Cards/EntityAction.vue";
import {Source} from "@/domain/type/source.type.ts";
import {useSynchroStore} from "@/stores/synchro.store.ts";
import {useWorkflowStore} from "@/stores/workflow.store.ts";
import {SyncState} from "@/domain/type/synchro.type.ts";
import router from "@/router/router.ts";
import {RouteEnum} from "@/router/RouteEnum.ts";

const store = useEntitiesStore()
const workflowStore = useWorkflowStore()
const props = defineProps<{
  ownerId?: string,
  type: string,
  sourceLeft: Source,
  sourceRight: Source
}>()
onBeforeMount(() => {
  useSynchroStore().id = props.ownerId ?? ''
  useWorkflowStore().companyId = props.ownerId ?? ''
  useEntitiesStore().ownerId = props.ownerId ?? ''
  useEntitiesStore().entityType = props.type
  useEntitiesStore().sourceLeft = props.sourceLeft
  useEntitiesStore().sourceRight = props.sourceRight
  store.fetchList(props.ownerId ?? null, props.type)
  workflowStore.type = props.type

  // Store courseNumGroup when on SESSIONS view
  if (props.type === 'session') {
    useWorkflowStore().courseNumGroup = props.ownerId ?? ''
    useWorkflowStore().courseId = props.ownerId ?? ''
  }
  if (
    router.currentRoute.value.query.filter &&
    Object.values(SyncState).includes(router.currentRoute.value.query.filter as SyncState)
  ) {
    router.currentRoute.value.name = RouteEnum.SyncWithoutOwner
    store.stateFilter = router.currentRoute.value.query.filter as SyncState
  }
})

function getQuery(value: SyncState | null | SyncState.OK | SyncState.DIFF | SyncState.OTHER_OWNER | SyncState.MISSING | SyncState.TOO_MANY | SyncState.DISABLED) {
  if (value) {
    if (props.ownerId) {
      return {
        filter: value,
        ownerId: props.ownerId
      }
    } else {
      return {
        filter: value
      }
    }
  } else {
    if (props.ownerId) {
      return {
        ownerId: props.ownerId
      }
    } else {
      return {}
    }
  }
}

function replaceRoute(query: { filter: SyncState.OK | SyncState.DIFF | SyncState.OTHER_OWNER | SyncState.MISSING | SyncState.TOO_MANY | SyncState.DISABLED; ownerId: string } | { filter: SyncState.OK | SyncState.DIFF | SyncState.OTHER_OWNER | SyncState.MISSING | SyncState.TOO_MANY | SyncState.DISABLED } | { ownerId: string } | {}) {
  if (props.ownerId) {
    router.replace({
      name: RouteEnum.SyncWithOwner,
      params: {
        type: props.type,
        sourceLeft: store.sourceLeft,
        sourceRight: store.sourceRight,
        ownerId: props.ownerId,
      },
      query: query
    })
  } else {
    router.replace({
      name: RouteEnum.SyncWithoutOwner,
      params: {
        'type': props.type,
        'sourceLeft': store.sourceLeft,
        'sourceRight': store.sourceRight,
      },
      query: query
    })
  }
}

const filter = computed({
  get: () => store.stateFilter,
  set: (value: SyncState | null) => {
    store.stateFilter = value
    let query = getQuery(value);
    replaceRoute(query);
  }
})

const searchLabel = computed(() => {
  return 'Recherche (' + store.filteredEntities.length + ')';
})

const loadingCards = computed(() => {
  return Array.from({length: 10}, () => ({}))
})

const filterItems = computed(() => {
  return [
    {text: 'Tous (' + store.countAll + ')', value: null},
    {text: 'Synchronisés (' + store.countOk + ')', value: SyncState.OK},
    {text: 'Différents (' + store.countDiff + ')', value: SyncState.DIFF},
    {text: 'Manquants (' + store.countMissing + ')', value: SyncState.MISSING},
    {text: 'Doublons (' + store.countTooMany + ')', value: SyncState.TOO_MANY},
    {text: 'Autre dépendance (' + store.countOtherOwner + ')', value: SyncState.OTHER_OWNER}
  ]
});
const sideCols = computed(() => {
  const {sm} = useDisplay()
  return sm.value ? 5 : 3
})
const centerCols = computed(() => {
  const {md, lg, xl, xxl} = useDisplay()
  return md.value ? 4 : lg ? 4 : xl ? 2 : xxl ? 1 : 4
})
</script>

<template>
  <v-container>
    <v-row align="center" class="mt-1 d-sm-flex d-md-none" justify="center">
      <v-col class="text-center">
        <p>Écran trop petit pour accéder à l'interface de synchronisation.</p>
      </v-col>
    </v-row>
    <v-row align="center" class="mt-1 d-none d-sm-none d-md-flex" justify="center">
      <v-col :cols="sideCols" class="d-flex justify-end src1">
        <img v-if="Source.EVA === store.sourceLeft" alt="Eva" class="evaLogo" src="@/assets/img/eva.png" style="width: 100px; margin-right: 100px;"/>
        <img v-else-if="Source.AMMON === store.sourceLeft" alt="Ammon" class="ammonLogo" src="@/assets/img/ammon.png" style="width: 110px; margin-right: 80px;"/>
      </v-col>
      <v-col :cols="centerCols" class="d-flex justify-center align-center flex-column">
        <v-container>
          <v-row no-gutters width="100%">
            <v-col cols="8" lg="7" md="12">
              <v-text-field
                v-model="store.search"
                :label="searchLabel"
                :style="{ background: 'white' }"
                density="compact"
                hide-details
                variant="filled"
                width="100%"
              />
            </v-col>
            <v-col cols="4" lg="5" md="12">
              <v-select
                v-model="filter"
                :items="filterItems"
                density="compact"
                hide-details
                item-title="text"
                item-value="value"
                variant="filled"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-col>
      <v-col :cols="sideCols" class="d-flex justify-start src2">
        <img v-if="Source.EVA === store.sourceRight" alt="Eva" class="evaLogo" src="@/assets/img/eva.png" style="width: 100px; margin-left: 100px;"/>
        <img v-else-if="Source.AMMON === store.sourceRight" alt="Ammon" class="ammonLogo" src="@/assets/img/ammon.png" style="width: 110px; margin-left: 80px;"/>
      </v-col>
    </v-row>
    <v-row v-for="() in loadingCards" v-if="null === store.entities" class="d-none d-sm-none d-md-flex" justify="center">
      <v-col :cols="sideCols" class="d-flex justify-end">
        <v-skeleton-loader elevation="2" style="width:300px;" type="image"/>
      </v-col>
      <v-col :cols="centerCols" class="d-flex justify-center align-center">
        <!--        <v-skeleton-loader type="image" elevation="2" style="width:180px;"/>-->
      </v-col>
      <v-col :cols="sideCols" class="d-flex justify-start">
        <v-skeleton-loader elevation="2" style="width:300px;" type="image"/>
      </v-col>
    </v-row>
    <v-row v-else-if="store.filteredEntities.length === 0" classs=" d-none d-sm-none d-md-flex" justify="center">
      <v-col class="d-flex justify-center align-center" cols="12">
        <h3 class="text-h6">Aucun résultat</h3>
      </v-col>
    </v-row>
    <v-row v-for="(entity) in store.filteredEntities" v-else class="mt-5 d-none d-sm-none d-md-flex cards-row" justify="center">
      <v-col :cols="sideCols" class="d-flex justify-end">
        <v-lazy :options="{'threshold':0.5}" transition="fade-transition">
          <details-card
            :details="entity.getLeftCardData()"
            :entity-uuid="entity.uuid"
            :icon="entity.cardIcon"
            :link="entity.leftLink"
            :show-error="false"
            :show-side-car="false"
            :status="entity.state"
            :type="type"
            class="leftCard"
            style="width: 300px; height: 100%;"/>
        </v-lazy>
      </v-col>
      <v-col :cols="centerCols" class="d-flex justify-center align-center">
        <v-lazy :options="{'threshold':0.5}" transition="fade-transition">
          <entity-action
            v-if="entity.hasAction()"
            :actions="entity.actions"
            :entity="entity"
            :owner-id="ownerId ?? ''"
            :src-left="store.sourceLeft"
            :src-right="store.sourceRight"
            class="mx-5 action"/>
        </v-lazy>
      </v-col>
      <v-col :cols="sideCols" class="d-flex justify-start">
        <v-lazy :options="{'threshold':0.5}" transition="fade-transition">
          <details-card
            :details="entity.getRightCardData()"
            :entity-uuid="entity.uuid"
            :icon="entity.cardIcon"
            :link="entity.rightLink"
            :show-error="true"
            :show-side-car="true"
            :status="entity.state"
            :type="type"
            class="rightCard"
            style="width: 300px; height:100%;"
          />
        </v-lazy>
      </v-col>
    </v-row>
  </v-container>
</template>
<style>

</style>
